import React from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Pages shown on each side of the current page. */
  siblingCount?: number;
  className?: string;
  previousLabel?: string;
  nextLabel?: string;
}

const DOTS = '...';

/**
 * Builds a windowed page list, e.g. [1, '...', 22, 23, 24, '...', 48],
 * so long result sets don't render one button per page.
 */
const getPageRange = (
  currentPage: number,
  totalPages: number,
  siblingCount: number
): (number | string)[] => {
  // first + last + current + 2 dots + siblings on both sides
  const totalShown = siblingCount * 2 + 5;

  if (totalPages <= totalShown) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < totalPages - 1;

  if (!showLeftDots && showRightDots) {
    const leftCount = siblingCount * 2 + 3;
    return [
      ...Array.from({ length: leftCount }, (_, index) => index + 1),
      DOTS,
      totalPages,
    ];
  }

  if (showLeftDots && !showRightDots) {
    const rightCount = siblingCount * 2 + 3;
    return [
      1,
      DOTS,
      ...Array.from(
        { length: rightCount },
        (_, index) => totalPages - rightCount + 1 + index
      ),
    ];
  }

  return [
    1,
    DOTS,
    ...Array.from(
      { length: rightSibling - leftSibling + 1 },
      (_, index) => leftSibling + index
    ),
    DOTS,
    totalPages,
  ];
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className = 'pagination justify-content-end me-3 mt-2 mb-0',
  previousLabel,
  nextLabel,
}: PaginationProps) => {
  const { t } = useTranslation();

  if (!totalPages || totalPages < 2) {
    return null;
  }

  const pages = getPageRange(currentPage, totalPages, siblingCount);

  return (
    <ul className={className}>
      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          {previousLabel ?? t('user.previous')}
        </button>
      </li>
      {pages.map((page, index) =>
        page === DOTS ? (
          <li key={`dots-${index}`} className="page-item disabled">
            <span className="page-link">{DOTS}</span>
          </li>
        ) : (
          <li
            key={page}
            className={`page-item ${currentPage === page ? 'active' : ''}`}
          >
            <button
              className="page-link"
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </button>
          </li>
        )
      )}
      <li
        className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
      >
        <button
          className="page-link"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {nextLabel ?? t('user.next')}
        </button>
      </li>
    </ul>
  );
};

export default Pagination;
